import { FC } from 'react';
import { Button, Col, Divider, Popover, Row, Tabs } from 'antd';
import { TbArrowRight, TbEdit, TbIcons } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import { EmptySection, LearnMoreFooter, VirtualizedList as List, MiddleDot, SmallTag } from '@/shared';
import { normalizeReportComponentConfiguration } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/configuration';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';

import { DraftChange, getDraftChanges } from './helpers';

interface FooterProps {
    component: ReportComponent;
    onSave: () => Promise<void>;
}

const Footer: FC<FooterProps> = ({ component, onSave }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const reportAccess = useReportComponentAccess(component);

    const { draftConfigurations, draftProperties, reportComponents } = useReportComponentStore();

    const draft = draftConfigurations[component?.id as string];
    const propertyDraft = draftProperties[component.id];
    const persistedComponent = reportComponents.find((item) => item.id === component.id) ?? component;

    const changes = getDraftChanges(
        {
            ...persistedComponent,
            configuration: normalizeReportComponentConfiguration(
                persistedComponent.configuration,
                persistedComponent.type,
            ),
        },
        draft,
        propertyDraft,
    );
    const hasDraft = Boolean(draft && propertyDraft);
    const pastChanges: DraftChange[] = [];
    const renderChanges = (items: DraftChange[], emptyDescription: string) =>
        items.length ? (
            <List
                size='small'
                dataSource={items}
                renderItem={(change) => (
                    <List.Item>
                        <div style={{ width: '100%' }}>
                            <strong>{change.field}</strong>
                            <Row gutter={[8, 0]} wrap={false} style={{ marginTop: '0.25rem' }}>
                                <Col flex='1 1 0' style={{ minWidth: 0 }}>
                                    <small style={{ color: token.colorTextTertiary }}>Before</small>
                                    <div
                                        style={{
                                            color: token.colorTextSecondary,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {String(change.before)}
                                    </div>
                                </Col>
                                <Col flex='none'>
                                    <TbArrowRight style={{ color: token.colorTextTertiary }} />
                                </Col>
                                <Col flex='1 1 0' style={{ minWidth: 0 }}>
                                    <small style={{ color: token.colorTextTertiary }}>After</small>
                                    <div
                                        style={{
                                            color: token.colorText,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {String(change.after)}
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </List.Item>
                )}
            />
        ) : (
            <EmptySection
                height={16 * 10}
                icon={<TbIcons size='75%' style={{ color: token.colorTextTertiary }} />}
                label='No Changes'
                description={emptyDescription}
            />
        );

    return (
        <Row align={'middle'} style={{ margin: '1rem 1.25rem' }}>
            <Col flex='none'>
                <LearnMoreFooter align='left' topic='Components' />
            </Col>
            <Col flex='auto' />
            {hasDraft && (
                <>
                    <Col flex='none'>
                        <Popover
                            placement='topRight'
                            arrow={false}
                            styles={{ body: { padding: 0 } }}
                            content={
                                <div style={{ width: '22rem', padding: '0.75rem' }}>
                                    <strong>Changes</strong>
                                    <Tabs
                                        size='small'
                                        style={{ marginTop: '0.25rem' }}
                                        items={[
                                            {
                                                key: 'active',
                                                label: 'Active Changes',
                                                children: renderChanges(changes, 'No active changes.'),
                                            },
                                            {
                                                key: 'past',
                                                label: 'Past Changes',
                                                children: renderChanges(
                                                    pastChanges,
                                                    'No previous saved changes are available for this session.',
                                                ),
                                            },
                                        ]}
                                    />
                                </div>
                            }
                        >
                            <span style={{ cursor: 'pointer' }}>
                                <SmallTag
                                    bordered
                                    compact
                                    icon={
                                        <TbEdit
                                            size='0.875rem'
                                            style={{ color: token.colorTextTertiary, marginRight: '0.25rem' }}
                                        />
                                    }
                                    message={
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {changes.length} Active Change{changes.length !== 1 ? 's' : ''}
                                            <MiddleDot horizontalMargin='0.25rem' />
                                            {pastChanges.length > 9 ? '9+' : pastChanges.length} Past Change
                                            {pastChanges.length !== 1 ? 's' : ''}
                                        </span>
                                    }
                                    styles={{ marginLeft: 0, height: '1.5rem', padding: '0 0.375rem' }}
                                />
                            </span>
                        </Popover>
                    </Col>
                    <Col flex='none'>
                        <Divider type='vertical' />
                    </Col>
                </>
            )}
            <Col flex='none'>
                <Button
                    type='primary'
                    size='small'
                    onClick={() => void onSave().catch(() => undefined)}
                    disabled={!hasDraft || !changes.length || component.locked || !reportAccess.canUpdate}
                >
                    Save
                    <TbArrowRight />
                </Button>
            </Col>
        </Row>
    );
};

export default Footer;
